import { z } from "zod";
import type { Accounting } from "../api/engage/engage.types";
import type { ShellUser } from "../api/types";
import type { Defined, Nullish } from "../types";
import type { BrowserName, DeviceType, OsName } from "./deviceInformation";

export const trackEventPayloadZod = z.object({
  id: z.string(),
  name: z.string(),
  appName: z.string(),
  action: z
    .union([
      z.literal("click"),
      z.literal("hover"),
      z.literal("focus"),
      z.literal("page_view"),
      z.literal("scroll"),
    ])
    .optional(),
  type: z.string().optional(),
  experimentInfo: z
    .object({
      experimentId: z.string(),
      bucketId: z.string(),
    })
    .optional(),
  source: z.string().url(),
  purposes: z
    .union([
      z.literal("Functional"),
      z.literal("Marketing"),
      z.literal("Analytical"),
    ])
    .array()
    // TODO: after 30th september 2024, purpose won't be "optional", see shouldEnforcePurpose function
    .optional(),
  specificInfo: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
    .optional(),
  customInfo: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
    .optional(),
  tag: z.string().optional(),
  target: z
    .union([
      z.literal("button"),
      z.literal("link"),
      z.literal("inputField"),
      z.literal("menu"),
      z.literal("tab"),
      z.literal("modal"),
    ])
    .optional(),
  description: z.string().optional(),
});

export type TrackEventPayload = z.infer<typeof trackEventPayloadZod>;

const trackEventZod = z.object({
  payload: trackEventPayloadZod,
});

export enum DataCollectors {
  Datadog,
  RudderStack,
  GA4,
}

export type TrackEvent = z.infer<typeof trackEventZod>;

export type ClientEvent = {
  eventInfo: {
    eventAction: Defined<TrackEvent["payload"]["action"]> | "";
    eventSessionId: string;
    eventTag: string;
    eventTarget: string;
    eventTimestamp: number;
    eventTracingId: TrackEvent["payload"]["id"];
    eventType: Defined<TrackEvent["payload"]["type"]>;
    eventValue: string;
  };
  userInfo: {
    userAccountId: Defined<Accounting["account_id"]>;
    userAccountName: Defined<Accounting["company"]>;
    userCountry: Defined<Accounting["company"]>;
    userId: ShellUser["id"];
    userLanguage: string;
    userProjectId: string;
    userRoles: string[];
    userTier: string;
    userTimezone: string;
  };
  deviceInfo: {
    deviceId: string;
    deviceType: DeviceType;
    deviceViewportSize: string;
    deviceOs: OsName;
    deviceOsVersion: string;
    deviceModel: string;
    deviceBrand: string;
    deviceBrowser: BrowserName;
    deviceBrowserVersion: string;
  };
  appInfo: {
    appName: string;
    appMfe: TrackEvent["payload"]["appName"];
    appVersion: string;
    appDomain: string;
  };
  pageInfo: {
    pageUrl: string;
    PrevPageURL: string;
  };
  experimentInfo: {
    experimentName: string;
    experimentBucket: string;
  };
  specificInfo:
    | TrackEvent["payload"]["customInfo"]
    | TrackEvent["payload"]["specificInfo"];
};

export type AnalyticsUser = Partial<
  ShellUser & {
    account?:
      | Partial<
          ShellUser["account"] & {
            country?: string | Nullish;
            createdAt?: string | Nullish;
            paymentType?: string | Nullish;
            currency?: string | Nullish;
            has2faEnabled?: boolean | Nullish;
            status?: string | Nullish;
            type?: string | Nullish;
            firstProduct?: string | Nullish;
            focusArea?: string | Nullish;
            billingType?: string | Nullish;
            verificationStatus?: string | Nullish;
            vendorId?: string | Nullish;
          }
        >
      | Nullish;
    createdAt?: string | Nullish;
    type?: "" | "N" | "L" | "S";
    hasPhoneInProfile?: boolean | Nullish;
    isSuperUser?: boolean | Nullish;
    isSmokeTester?: boolean | Nullish;
    app?: {
      vendorId?: string;
    };
    defaultCountryCode?: string;
    hashedEmail?: string;
    trackingId?: string | Nullish;
    persona?: string | Nullish;
    goal?: string | Nullish;
    marketingPlatform?: string | Nullish;
    programmingLanguage?: string | Nullish;
    codeExperience?: string | Nullish;
    firstProduct?: string | Nullish;
    role?: string | string[] | Nullish;
  }
>;

/** Data provided to the event by the shell, !It can be platform specific! */
export type EventShellData = {
  user: Partial<AnalyticsUser>;
} & Partial<{
  account: Partial<Accounting>;
  navigationItem: {
    id: string;
  };
}>;
